import { useEffect, useState, useMemo, useCallback } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import { formatTwoDecimals } from "../../global/money";
import {
  getInvoices,
  sendInvoiceToFbr,
  deleteInvoices,
  cancelInvoiceStatus,
  getInvoiceHistory,
  toggleInvoiceVisibility,
} from "../../Actions/InvoicesAction";
import CancelInvoiceModal from "./CancelInvoiceModal";
import InvoiceHistoryModal from "./InvoiceHistoryModal";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  IconButton,
  Tooltip,
  Box,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import Swal from "sweetalert2";
import { isUndefined, isNull, debounce } from "lodash";
import { generateInvoice } from "./generateInvoice";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import { CompanySearchField } from "../Common/CompanySearchField";
import { getCompany } from "../../Actions/CompanyAction";
import moment from "moment";

// An invoice is filed against either FBR's live or sandbox gateway, decided by
// the company it belongs to. Green reads as "this is real", amber as "this is a
// test" - the same pairing the mobile app and the PDF use.
const ENVIRONMENT_CHIP = {
  Production: { label: "Production", color: "success" },
  Sandbox: { label: "Sandbox", color: "warning" },
};

const environmentChipProps = (companyStatus) =>
  ENVIRONMENT_CHIP[companyStatus] ?? {
    label: companyStatus || "—",
    color: "default",
  };

// "Save Invoice Only" leaves an invoice FBR has never been asked about, and
// that is what this looks for: no verdict stored, and not Valid or Cancelled.
// saveInvoiceStatus cannot answer it - it keeps its value once the invoice is
// posted later, an edit rewrites it and a bulk upload never sets it.
const isSavedOnly = (invoice) =>
  !invoice?.FBRInvoiceStatusResponse &&
  !["valid", "cancel"].includes(
    String(invoice?.FBRInvoiceStatus ?? "").toLowerCase(),
  );

const SAVED_ONLY_CHIP = { label: "Saved Only", color: "info" };

const InvoicesLIst = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [companyStatus, setCompanyStatus] = useState("");
  const [FBRInvoiceStatus, setFBRInvoiceStatus] = useState("");
  // The Company search: {}, { companyId } or { companyName } (CompanySearchField).
  const [companyFilter, setCompanyFilter] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelInvoiceId, setCancelInvoiceId] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const isGettingInvoicesList = useSelector(
    (state) => state.InvoicesListReducer?.isGettingInvoicesList,
  );
  const invoicesList = useSelector(
    (state) => state.InvoicesListReducer?.invoicesList || [],
  );
  const totalResults = useSelector(
    (state) => state.InvoicesListReducer?.totalResults || 0,
  );
  const invoiceHistory = useSelector(
    (state) => state.InvoicesListReducer?.invoiceHistory || [],
  );
  const isGettingInvoiceHistory = useSelector(
    (state) => state.InvoicesListReducer?.isGettingInvoiceHistory,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const { userDetails } = useSelector((state) => state?.HeaderReducer);
  const isAdmin = userDetails?.role === "admin";

  useEffect(() => {
    dispatch(
      getInvoices(
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        companyStatus,
        FBRInvoiceStatus,
        companyFilter,
      ),
    );
  }, [
    pagination,
    dispatch,
    globalFilter,
    companyStatus,
    FBRInvoiceStatus,
    companyFilter,
  ]);

  // The companies the Company search suggests.
  useEffect(() => {
    dispatch(getCompany("", "", ""));
  }, [dispatch]);

  // A new search or filter is a different result set, so it is read from its
  // first page - the page the user was on may not exist in it.
  const toFirstPage = useCallback(
    () =>
      setPagination((prev) =>
        prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
      ),
    [],
  );

  // The page the user is on can stop existing: the last row of the last page
  // is deleted, or posted while the Invalid filter is on. Step back to the last
  // page that still has rows instead of leaving an empty table.
  useEffect(() => {
    if (isGettingInvoicesList) return;
    const lastPage = Math.max(
      0,
      Math.ceil(totalResults / pagination.pageSize) - 1,
    );
    if (pagination.pageIndex > lastPage) {
      setPagination((prev) => ({ ...prev, pageIndex: lastPage }));
    }
  }, [isGettingInvoicesList, totalResults, pagination]);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value) => {
        setGlobalFilter(value);
        toFirstPage();
      }, 500),
    [toFirstPage],
  );

  // A new company search is read from its first page, like any other filter.
  const handleCompanySearch = useCallback(
    (filter) => {
      setCompanyFilter(filter);
      toFirstPage();
    },
    [toFirstPage],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  const swalHtml = (msg, type, data) => {
    if (type === "success") return `<p>${msg}</p>`;
    else if (
      type === "error" &&
      (!data?.invoiceStatuses || data.invoiceStatuses.length === 0)
    )
      return `<p>${data?.error}</p>`;
    else if (type === "error" && !isNull(data?.invoiceStatuses)) {
      return `
            <ul>
              ${data?.invoiceStatuses.map(
        (element) => `<li>${element.error}</li><br />`,
      )}
            </ul>
           `;
    }
  };

  const handelVisibleForAddInvoice = (msg, type, data) => {
    Swal.fire({
      title:
        type === "success"
          ? "INVOICE SUBMISSION TO FBR SUCESSFULL"
          : "INVOICE SUBMISSION TO FBR UNSUCESSFUL",
      html: swalHtml(msg, type, data),
      icon: type === "success" ? "success" : "error",
    });
  };

  const handleCancelInvoice = (id) => {
    setCancelInvoiceId(id);
    setCancelModalOpen(true);
  };

  const handleOpenHistory = (id) => {
    dispatch(getInvoiceHistory(id, handleDangerVisible));
    setHistoryModalOpen(true);
  };

  const handleToggleVisibility = (id) => {
    dispatch(
      toggleInvoiceVisibility(
        id,
        handleVisible,
        handleDangerVisible,
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        companyStatus,
        FBRInvoiceStatus,
        companyFilter,
      ),
    );
  };

  const handleConfirmCancel = () => {
    dispatch(
      cancelInvoiceStatus(
        cancelInvoiceId,
        handleVisible,
        handleDangerVisible,
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        companyStatus,
        FBRInvoiceStatus,
        companyFilter,
      ),
    );
    setCancelModalOpen(false);
    setCancelInvoiceId(null);
  };

  const handleDeleteInvoices = (row, type) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete ${type === "single" ? "this" : "these"} record?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "mui-swal-popup",
      },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) {
          container.style.zIndex = "2000";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteInvoices(
            type === "single"
              ? { invoiceIds: [row?.original?._id] }
              : { invoiceIds: row?.map((item) => item?._id) },
            handleVisible,
            handleDangerVisible,
            type,
            type === "bulk" && setRowSelection,
            pagination.pageIndex,
            pagination.pageSize,
            globalFilter,
            companyStatus,
            FBRInvoiceStatus,
            companyFilter,
          ),
        );
      }
    });
  };

  const handlePostToFBRInvoice = (row, type) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to Post ${type === "single" ? "this" : "these"} record to FBR?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Post it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2fd33a",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "mui-swal-popup",
      },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) {
          container.style.zIndex = "2000";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          sendInvoiceToFbr(
            type === "single"
              ? { invoiceIds: [row?.original?._id] }
              : { invoiceIds: row?.map((item) => item?._id) },
            handelVisibleForAddInvoice,
            handleDangerVisible,
            type,
            type === "bulk" && setRowSelection,
            pagination.pageIndex,
            pagination.pageSize,
            globalFilter,
            companyStatus,
            FBRInvoiceStatus,
            companyFilter,
          ),
        );
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 60,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "companyId.companyName",
        header: "Company Name",
        size: 150, // slightly wider
        grow: 0, // fixed width
        enableEditing: false,
      },
      {
        accessorKey: "invoiceType",
        header: "Invoice Type",
        size: 150,
        grow: 0, // take flexible space
        enableEditing: false,
      },
      {
        accessorKey: "FBRInvoiceStatus",
        header: "FBR Invoice Status",
        grow: true,
        enableEditing: false,
        Cell: ({ cell, row }) => {
          const status = String(cell.getValue() ?? "")?.toLowerCase();
          const statusConfig = {
            valid: { label: "Valid", color: "success" },
            invalid: { label: "Invalid", color: "error" },
            cancel: { label: "Cancelled", color: "warning" },
          };
          const config = statusConfig[status] ?? {
            label: cell.getValue() ?? "—",
            color: "default",
          };
          return (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              <Chip
                label={config.label}
                color={config.color}
                size="small"
                variant="filled"
              />
              {isSavedOnly(row.original) && (
                <Chip {...SAVED_ONLY_CHIP} size="small" variant="filled" />
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "companyStatus",
        header: "Environment",
        size: 130,
        grow: 0,
        enableEditing: false,
        Cell: ({ cell }) => (
          <Chip
            {...environmentChipProps(cell.getValue())}
            size="small"
            variant="filled"
          />
        ),
      },
      {
        accessorKey: "date",
        header: "Invoice Date",
        size: 150,
        grow: 0,
        enableEditing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "";
          return moment(value).format("DD-MM-YYYY");
        },
      },
      {
        accessorKey: "buyerId.businessName",
        header: "Buyer Name",
        size: 150,
        grow: 0,
        enableEditing: false,
      },
      {
        accessorKey: "invoiceNumber",
        header: "Invoice Number",
        size: 150,
        grow: 0,
        enableEditing: false,
      },
      {
        accessorKey: "FBRInvoiceRefNo",
        header: "FBR INV NUMBER",
        size: 150,
        grow: 0,
        enableEditing: false,
      },
      {
        accessorKey: "subTotal",
        header: "Before Tax",
        Cell: ({ cell }) => formatTwoDecimals(cell.getValue()),
        size: 150,
        grow: 0, // take flexible space
        enableEditing: false,
      },
      {
        accessorKey: "grandTotal",
        header: "After Tax",
        Cell: ({ cell }) => formatTwoDecimals(cell.getValue()),
        size: 150,
        grow: 0, // take flexible space
        enableEditing: false,
      },
      {
        accessorKey: "createdBy.email",
        header: "Created By",
        grow: true,
        enableEditing: false,
      },
      {
        id: "actions",
        header: "Actions",
        size: 260,
        minSize: 260,
        grow: 0, // fixed width
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box display="flex" gap={0.5} justifyContent={"center"} flexWrap="nowrap">
            <Tooltip title="Generate Invoice">
              <IconButton
                size="small"
                onClick={() => generateInvoice(row.original)}
                sx={{ color: "black" }}
              >
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <Tooltip title="Invoice History">
                <IconButton
                  size="small"
                  onClick={() => handleOpenHistory(row.original._id)}
                  sx={{ color: "black" }}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isAdmin && (
              <Tooltip title={row?.original?.isVisible === false ? "Activate Invoice" : "Hide Invoice"}>
                <IconButton
                  size="small"
                  onClick={() => handleToggleVisibility(row?.original?._id)}
                  sx={{ color: row?.original?.isVisible === false ? "success.main" : "text.secondary" }}
                >
                  {row?.original?.isVisible === false ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {row?.original?.FBRInvoiceStatusResponse?.validationResponse
              ?.status === "Valid" &&
              row?.original?.FBRInvoiceStatus?.toLowerCase() !== "cancel" && (
                <Tooltip title="Cancel Status">
                  <IconButton
                    size="small"
                    onClick={() => handleCancelInvoice(row?.original?._id)}
                    sx={{ color: "black" }}
                  >
                    <CancelPresentationIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            {(!row?.original?.FBRInvoiceStatusResponse ||
              row?.original?.FBRInvoiceStatusResponse?.validationResponse
                ?.status === "Invalid") && (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate("/main/edit-invoice", { state: row.original })
                      }
                      sx={{ color: "black" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Post To FBR">
                    <IconButton
                      size="small"
                      onClick={() => handlePostToFBRInvoice(row, "single")}
                      sx={{ color: "black" }}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteInvoices(row, "single")}
                      sx={{ color: "black" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
          </Box>
        ),
      },
    ],
    // The "#" cell and the row actions read the page and filters in force, so
    // the columns are rebuilt with them. Built once, they kept the first page
    // and no filters for good: numbering restarted on every page and a row
    // action reloaded page one.
    // eslint-disable-next-line
    [
      isAdmin,
      pagination,
      globalFilter,
      companyStatus,
      FBRInvoiceStatus,
      companyFilter,
    ],
  );
  const renderMobileInvoiceCards = () => {
    return invoicesList.map((item, index) => {
      const serial = index + 1 + pagination.pageIndex * pagination.pageSize;
      const isInvalidOrNotPosted =
        !item?.FBRInvoiceStatusResponse ||
        item?.FBRInvoiceStatusResponse?.validationResponse?.status ===
        "Invalid";
      return (
        <Box
          key={item._id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {/* Top Row: Serial + Actions */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box sx={{ fontSize: 12, color: "#999" }}>#{serial}</Box>

            <Box display="flex" gap={1}>
              {/* Generate PDF */}
              <Tooltip title="Generate Invoice">
                <IconButton
                  size="small"
                  onClick={() => generateInvoice(item)}
                  sx={{ color: "black" }}
                >
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {isAdmin && (
                <Tooltip title="Invoice History">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenHistory(item._id)}
                    sx={{ color: "black" }}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {isAdmin && (
                <Tooltip title={item?.isVisible === false ? "Activate Invoice" : "Hide Invoice"}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleVisibility(item._id)}
                    sx={{ color: item?.isVisible === false ? "success.main" : "text.secondary" }}
                  >
                    {item?.isVisible === false ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {item?.FBRInvoiceStatusResponse?.validationResponse?.status ===
                "Valid" &&
                item?.FBRInvoiceStatus?.toLowerCase() !== "cancel" && (
                  <Tooltip title="Cancel Status">
                    <IconButton
                      size="small"
                      onClick={() => handleCancelInvoice(item._id)}
                      sx={{ color: "black" }}
                    >
                      <CancelPresentationIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

              {isInvalidOrNotPosted && (
                <>
                  {/* Edit */}
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate("/main/edit-invoice", { state: item })
                      }
                      sx={{ color: "black" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Post to FBR */}
                  <Tooltip title="Post To FBR">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handlePostToFBRInvoice({ original: item }, "single")
                      }
                      sx={{ color: "black" }}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Delete */}
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleDeleteInvoices({ original: item }, "single")
                      }
                      sx={{ color: "black" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>

          {/* Company */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Company:</b>
            <Box sx={{ fontSize: 14 }}>
              {item?.companyId?.companyName || "-"}
            </Box>
          </Box>

          {/* Invoice Type */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Type:</b>
            <Box sx={{ fontSize: 14 }}>{item.invoiceType || "-"}</Box>
          </Box>

          {/* FBR Invoice Status */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              FBR Invoice Status:
            </b>
            <Box sx={{ fontSize: 14, mt: 0.5 }}>
              {(() => {
                const status = String(
                  item?.FBRInvoiceStatusResponse?.validationResponse?.status ??
                  "",
                ).toLowerCase();

                const statusConfig = {
                  valid: { label: "Valid", color: "success" },
                  invalid: { label: "Invalid", color: "error" },
                  cancel: { label: "Cancelled", color: "warning" },
                };

                const config = statusConfig[status] ?? {
                  label:
                    item?.FBRInvoiceStatusResponse?.validationResponse
                      ?.status || "—",
                  color: "default",
                };

                return (
                  <Chip
                    label={config.label}
                    color={config.color}
                    size="small"
                    variant="filled"
                  />
                );
              })()}
              {isSavedOnly(item) && (
                <Chip
                  {...SAVED_ONLY_CHIP}
                  size="small"
                  variant="filled"
                  sx={{ ml: 0.5 }}
                />
              )}
            </Box>
          </Box>

          {/* Environment */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Environment:</b>
            <Box sx={{ fontSize: 14, mt: 0.5 }}>
              <Chip
                {...environmentChipProps(item?.companyStatus)}
                size="small"
                variant="filled"
              />
            </Box>
          </Box>

          {/* Date */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Date:</b>
            <Box sx={{ fontSize: 14 }}>{item.date || "-"}</Box>
          </Box>

          {/* Buyer */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Buyer:</b>
            <Box sx={{ fontSize: 14 }}>
              {item?.buyerId?.businessName || "-"}
            </Box>
          </Box>

          {/* Invoice No */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Invoice #:</b>
            <Box sx={{ fontSize: 14 }}>{item.invoiceNumber || "-"}</Box>
          </Box>

          {/* FBR Ref */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>FBR Ref:</b>
            <Box sx={{ fontSize: 14 }}>{item.FBRInvoiceRefNo || "-"}</Box>
          </Box>

          {/* Amounts */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Before Tax:</b>
            <Box sx={{ fontSize: 14 }}>{formatTwoDecimals(item.subTotal)}</Box>
          </Box>

          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>After Tax:</b>
            <Box sx={{ fontSize: 14 }}>{formatTwoDecimals(item.grandTotal)}</Box>
          </Box>

          {/* Created By */}
          <Box>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Created By:</b>
            <Box sx={{ fontSize: 14 }}>{item?.createdBy?.email || "-"}</Box>
          </Box>
        </Box>
      );
    });
  };
  const isRowSelectable = useCallback((row) => {
    return (
      !row?.original?.FBRInvoiceStatusResponse ||
      row?.original?.FBRInvoiceStatusResponse?.validationResponse?.status ===
      "Invalid"
    );
  }, []);
  const renderTopToolbarCustomActions = useCallback(({ table }) => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedData = selectedRows.map((row) => row.original);

    return (
      <Box display="flex" gap={2} justifyContent={"center"}>
        <Tooltip title="Post To FBR">
          <span>
            <IconButton
              size="small"
              disabled={!selectedData.length}
              onClick={() => handlePostToFBRInvoice(selectedData, "bulk")}
              sx={{ color: "black" }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton
              size="small"
              disabled={!selectedData.length}
              onClick={() => handleDeleteInvoices(selectedData, "bulk")}
              sx={{ color: "black" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
    // Same reason as `columns`: the bulk actions reload the page in force.
    // eslint-disable-next-line
  }, [pagination, globalFilter, companyStatus, FBRInvoiceStatus, companyFilter]);

  const sortedInvoices = useMemo(() => {
    if (!(Object.keys(rowSelection).length > 0)) return invoicesList;
    return [...invoicesList].sort((a, b) => {
      const aInvalid =
        !a?.FBRInvoiceStatusResponse ||
        a?.FBRInvoiceStatusResponse?.validationResponse?.status === "Invalid";
      const bInvalid =
        !b?.FBRInvoiceStatusResponse ||
        b?.FBRInvoiceStatusResponse?.validationResponse?.status === "Invalid";
      if (!aInvalid && bInvalid) return 1;
      if (aInvalid && !bInvalid) return -1;
      return 0;
    });
  }, [invoicesList, rowSelection]);
  return (
    <Box className="p-4">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap", // important for mobile
          gap: 1,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          sx={{ width: "100%" }}
        >
          <Box>
            <Box
              component="h1"
              sx={{
                fontFamily: globalStyle.fontFamilyHeading,
                fontWeight: 700,
                color: globalStyle.bgColorBlue,
                fontSize: {
                  xs: globalStyle.headingFontSizeSmall,
                  sm: globalStyle.headingFontSize,
                },
                lineHeight: 1.2,
              }}
            >
              Invoices List
            </Box>
            <Box
              component="p"
              sx={{
                mt: 0.5,
                fontFamily: globalStyle.fontFamilyParagraph,
                fontSize: {
                  xs: 12,
                  sm: 14,
                },
                color: "#666",
              }}
            >
              Manage and view all Invoices records
            </Box>
          </Box>
          <Box
            display="flex"
            gap={2} // space between groups
            sx={{
              marginLeft: isMobile ? "0" : "auto",
              justifyContent: isMobile ? "center" : "flex-end",
              width: isMobile ? "100%" : "auto",
              flexWrap: "wrap",
            }}
          >
            <CompanySearchField
              companies={companiesList}
              onSearch={handleCompanySearch}
            />
            <Box display="flex" gap={1}>
              <Button
                variant={
                  companyStatus === "Production" ? "contained" : "outlined"
                }
                onClick={() => {
                  setCompanyStatus(
                    companyStatus === "Production" ? "" : "Production",
                  );
                  toFirstPage();
                }}
              >
                {isGettingInvoicesList ? (
                  <CircularProgress sx={{ color: "black" }} size={22} />
                ) : (
                  "Production"
                )}
              </Button>

              <Button
                variant={companyStatus === "Sandbox" ? "contained" : "outlined"}
                onClick={() => {
                  setCompanyStatus(
                    companyStatus === "Sandbox" ? "" : "Sandbox",
                  );
                  toFirstPage();
                }}
              >
                {isGettingInvoicesList ? (
                  <CircularProgress sx={{ color: "black" }} size={22} />
                ) : (
                  "Sandbox"
                )}
              </Button>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant={
                  FBRInvoiceStatus === "Valid" ? "contained" : "outlined"
                }
                onClick={() => {
                  setFBRInvoiceStatus(
                    FBRInvoiceStatus === "Valid" ? "" : "Valid",
                  );
                  toFirstPage();
                }}
              >
                {isGettingInvoicesList ? (
                  <CircularProgress sx={{ color: "black" }} size={22} />
                ) : (
                  "Valid"
                )}
              </Button>

              <Button
                variant={
                  FBRInvoiceStatus === "Invalid" ? "contained" : "outlined"
                }
                onClick={() => {
                  setFBRInvoiceStatus(
                    FBRInvoiceStatus === "Invalid" ? "" : "Invalid",
                  );
                  toFirstPage();
                }}
              >
                {isGettingInvoicesList ? (
                  <CircularProgress sx={{ color: "black" }} size={22} />
                ) : (
                  "Invalid"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingInvoicesList}
          searchLabel="Invoices"
        >
          {renderMobileInvoiceCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={sortedInvoices}
          getRowId={(row) => row._id}
          enableRowActions={false}
          manualPagination
          rowCount={totalResults}
          enableRowSelection={isRowSelectable}
          onRowSelectionChange={(updater) => {
            setRowSelection((old) => {
              const newState =
                typeof updater === "function" ? updater(old) : updater;
              return newState;
            });
          }}
          renderTopToolbarCustomActions={renderTopToolbarCustomActions}
          state={{
            columnOrder: [
              "mrt-row-select",
              "_id",
              "companyId.companyName",
              "invoiceType",
              "FBRInvoiceStatus",
              "date",
              "buyerId.businessName",
              "invoiceNumber",
              "FBRInvoiceRefNo",
              "subTotal",
              "grandTotal",
              "createdBy.email",
              "actions",
            ],
            columnVisibility: {
              actions: Object.keys(rowSelection).length === 0,
            },
            columnPinning: {
              right: ["actions"],
            },
            isLoading: isGettingInvoicesList,
            pagination,
            rowSelection,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
              whiteSpace: "normal",
              // wordBreak: "break-word",
              wordWrap: "break-word",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            component: Tooltip,
            title:
              row?.original?.FBRInvoiceStatus === "Invalid"
                ? row?.original?.FBRInvoiceStatusResponse?.validationResponse
                  ?.invoiceStatuses?.[0]?.error ||
                row?.original?.FBRInvoiceStatusResponse?.validationResponse
                  ?.error
                : "",
            placement: "top",
            arrow: true,
            componentsProps: {
              tooltip: {
                sx: {
                  backgroundColor: "#d32f2f", // tooltip bg
                  color: "#fff", // text color
                  fontSize: "14px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  maxWidth: "250px",
                  textAlign: "center",
                },
              },
              arrow: {
                sx: {
                  color: "#d32f2f", // arrow color same as bg
                },
              },
            },
            sx: {
              backgroundColor:
                row?.original?.FBRInvoiceStatus === "Invalid"
                  ? "#e08aa4"
                  : globalStyle.bgColorWhite,
              "&:hover": {
                backgroundColor:
                  row?.original?.FBRInvoiceStatus === "Invalid"
                    ? "#d298a9"
                    : globalStyle.bgColor,
              },
            },
          })}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none", // ✅ remove height restriction
              overflow: "visible", // ✅ allow content to expand
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
            },
          }}
        />
      )}
      <CancelInvoiceModal
        open={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setCancelInvoiceId(null);
        }}
        onConfirm={handleConfirmCancel}
      />
      <InvoiceHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        history={invoiceHistory}
        isLoading={isGettingInvoiceHistory}
      />
    </Box>
  );
};

export default InvoicesLIst;
